import React, { useEffect, useState } from "react";
import FeatureCardContent from "@shared/FeatureCardContent/FeatureCardContent";
import documentFileIcon from "@icons/document-file-icon.svg";
import { getSeasonalTheme } from "@lib/seasonalTheme";
import "./DiligenceRoomCard.css";

// Folder set mirrors the financing disclosure checklist the agent produces:
// ownership, governance approvals, financing instruments, IP, and metrics, with
// each item marked source-verified, missing, or awaiting founder or counsel
// confirmation. The two open items are the gaps the cap-table reconciliation
// workflow surfaces most often. Row count is deliberately unchanged - the mock
// body clips overflow.
const folders = [
  {
    name: "Corporate & governance",
    items: [
      { file: "Certificate of Incorporation.pdf", state: "ready" },
      { file: "Bylaws.pdf", state: "ready" },
      { file: "Initial board + stockholder consents (3).zip", state: "ready" },
    ],
  },
  {
    name: "Ownership & instruments",
    items: [
      { file: "Stock ledger - through latest issuance.xlsx", state: "ready" },
      {
        file: "Executed SAFEs.zip",
        state: "pending",
        note: "Signed copies outstanding",
      },
      {
        file: "Option grant notices (12/14).zip",
        state: "review",
        note: "2 post-formation hires",
      },
    ],
  },
  {
    name: "IP & metrics",
    items: [
      { file: "PIIA agreements (14/14).zip", state: "ready" },
      { file: "MRR / ARR waterfall - source reviewed.xlsx", state: "ready" },
    ],
  },
];

const stateLabel = { ready: "Ready", pending: "Pending", review: "In review" };

function DiligenceRoomCard() {
  const theme = getSeasonalTheme();
  const totalItems = folders.reduce((a, f) => a + f.items.length, 0);
  const ready = folders.reduce(
    (a, f) => a + f.items.filter((i) => i.state === "ready").length,
    0,
  );
  const targetPct = Math.round((ready / totalItems) * 100);

  const [pct, setPct] = useState(0);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / 1400, 1);
      const eased = 1 - (1 - t) ** 3;
      setPct(targetPct * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [targetPct]);

  return (
    <article
      className="venture-card diligence-card"
      style={{ "--card-monet": `url(${theme.paintings.diligence})` }}
    >
      <FeatureCardContent
        className="feature-card-content--startup-card"
        badgeTitle="Diligence Automation"
        title="A data room that organizes itself."
        text="As startups close customers, hire, & sign contracts, Vienna organizes traction & docs into a structured diligence room - as a shareable link ready for investor requests."
        icon={
          <img className="venture-card__feature-icon" src={documentFileIcon} alt="" />
        }
      >
        <div className="feature-card-content__tags">
          <span className="feature-card-content__tag">Corporate</span>
          <span className="feature-card-content__tag">Ownership</span>
          <span className="feature-card-content__tag">IP</span>
        </div>
      </FeatureCardContent>

      <div className="venture-card__pane venture-card__pane--visual">
        <div className="venture-mock diligence-mock">
          <div className="venture-mock__header">
            <span>Data room - Apex</span>
            <span className="diligence-mock__pct">{Math.round(pct)}% ready</span>
          </div>
          <div className="venture-mock__body diligence-mock__body">
            <div className="diligence-mock__progress" aria-hidden="true">
              <span style={{ width: `${pct}%` }} />
            </div>
            {folders.map((folder) => (
              <div key={folder.name} className="diligence-mock__folder">
                <div className="diligence-mock__folder-name">
                  <span className="diligence-mock__folder-icon" aria-hidden="true">
                    {"\u25B8"}
                  </span>
                  {folder.name}
                  <span className="diligence-mock__folder-count">
                    {folder.items.length}
                  </span>
                </div>
                <ul>
                  {folder.items.map((item) => (
                    <li
                      key={item.file}
                      className={`diligence-mock__item diligence-mock__item--${item.state}`}
                    >
                      <span className="diligence-mock__file">{item.file}</span>
                      <span className="diligence-mock__status">
                        {stateLabel[item.state]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

export default DiligenceRoomCard;
